import styled from 'styled-components';
import { useState } from 'react';
import {
    Checkbox,
    FormControlLabel,
    TextField,
    useMediaQuery,
} from '@mui/material';
import { TFormValues } from '../../App';
import { StepContainer } from '../StepContainer';
import { SubmitButton } from '../SubmitButton';
import { FileUploader } from '../FileUploader';
import { SubTitle } from '../SubTitle';
import { ErrorBox } from '../ErrorBox';
import { Footnote } from '../Footnote';
import { Row } from '../Row';
import { ContactPreferenceSelector } from '../ContactPreferenceSelector';
import { Spinner } from '../../svg';
import { createMarkup } from '../../utils';
import { breakpoints, colors, emailSubjects } from '../../constants';

const CheckboxContainer = styled.div``;
const Disclaimer = styled.p``;

interface IProps {
    className?: string;
    file: File | null;
    formValues: TFormValues;
    loading: boolean;
    isValid: boolean;
    isAvgSubject: boolean;
    subject: string;
    submitError: string;
    title?: string;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    onFileChange(file: File | null): void;
    setFieldValue(field: keyof TFormValues, value: string): void;
}

const BaseMessageForm = ({
    className,
    file,
    formValues,
    loading,
    isValid,
    submitError,
    isAvgSubject,
    subject,
    title,
    getFieldError,
    onFileChange,
    setFieldValue,
}: IProps) => {
    const mediumBreakpoint = useMediaQuery(`(${breakpoints.mediumMin})`);
    const [removeConfirmed, setRemoveConfirmed] = useState(false);

    const isDeleteSubject = subject === emailSubjects.deletePersonalInfo;
    const showContactPreference = subject === emailSubjects.complaint;

    const submitButtonDisabled = isDeleteSubject
        ? !isValid || !removeConfirmed
        : !isValid;

    const {
        firstName,
        lastNamePrefix,
        lastName,
        message,
        customerEmail,
        phoneNumber,
        contactPreference,
    } = formValues;

    const disclaimers = {
        [emailSubjects.question]:
            'We zullen je gegevens enkel gebruiken voor het afhandelen van je vraag.',
        [emailSubjects.complaint]:
            'We zullen je gegevens enkel gebruiken voor het afhandelen van je klacht.',
        [emailSubjects.change]:
            'We zullen je gegevens enkel gebruiken voor het afhandelen van je wijziging.',
        [emailSubjects.compliment]:
            'We zullen je gegevens enkel gebruiken voor het afhandelen van je compliment.',
        [emailSubjects.deletePersonalInfo]:
            'We hebben bovenstaande contactgegevens nodig om je te identificeren zodat wij de juiste persoon uit onze systemen kunnen verwijderen.',
        [emailSubjects.viewPersonalInfo]:
            'We zullen je gegevens enkel gebruiken voor het afhandelen van je aanvraag.',
        [emailSubjects.movePersonalInfo]:
            'We zullen je gegevens enkel gebruiken voor het afhandelen van je aanvraag.',
    };

    const messageLabels = {
        // Only non-AVG subjects
        [emailSubjects.question]: 'Je vraag',
        [emailSubjects.complaint]: 'Je klacht',
        [emailSubjects.change]: 'Je wijziging',
        [emailSubjects.compliment]: 'Je compliment',
    };

    return (
        <div className={className}>
            <StepContainer>
                {title && (
                    <h3>
                        Je <strong>contactgegevens</strong>
                    </h3>
                )}
                {isDeleteSubject && (
                    <>
                        <Disclaimer>
                            Let op! Na het verwijderen van je gegevens is het
                            voor ons niet meer mogelijk gegevens over
                            bijvoorbeeld je aankopen en oogmetingen te kunnen
                            raadplegen, waaronder je bon die nodig is voor de
                            garantie. Wil je liever geen e-mail meer ontvangen,
                            dan kun je je afmelden onder elke mail van Pearle
                            middels de afmeldlink.
                        </Disclaimer>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={removeConfirmed}
                                    id="removeConfirmed"
                                    onChange={(e) =>
                                        setRemoveConfirmed(e.target.checked)
                                    }
                                />
                            }
                            label="Ja, ik wil dat jullie mijn gegevens definitief verwijderen. *"
                            sx={{ margin: '30px 0 40px' }}
                        />
                    </>
                )}
                <Row>
                    <TextField
                        error={!!getFieldError('firstName')}
                        helperText={getFieldError('firstName')}
                        id="firstName"
                        label="Voornaam *"
                        value={firstName}
                        onChange={(e) =>
                            setFieldValue('firstName', e.target.value)
                        }
                    />
                    <TextField
                        error={!!getFieldError('lastNamePrefix')}
                        helperText={getFieldError('lastNamePrefix')}
                        id="lastNamePrefix"
                        label="Tussenvoegsel"
                        sx={{
                            margin: `${mediumBreakpoint && '0 10px'}`,
                            width: `${mediumBreakpoint && '50%'}`,
                        }}
                        value={lastNamePrefix}
                        onChange={(e) =>
                            setFieldValue('lastNamePrefix', e.target.value)
                        }
                    />
                    <TextField
                        error={!!getFieldError('lastName')}
                        helperText={getFieldError('lastName')}
                        id="lastName"
                        label="Achternaam *"
                        value={lastName}
                        onChange={(e) =>
                            setFieldValue('lastName', e.target.value)
                        }
                    />
                </Row>
                {showContactPreference && (
                    <TextField
                        error={!!getFieldError('phoneNumber')}
                        helperText={getFieldError('phoneNumber')}
                        id="phoneNumber"
                        label={'Telefoonnummer *'}
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) =>
                            setFieldValue('phoneNumber', e.target.value)
                        }
                    />
                )}
                <TextField
                    error={!!getFieldError('customerEmail')}
                    helperText={getFieldError('customerEmail')}
                    id="customerEmail"
                    label="E-mailadres *"
                    value={customerEmail}
                    onChange={(e) =>
                        setFieldValue('customerEmail', e.target.value)
                    }
                />
                {title && (
                    <h3
                        dangerouslySetInnerHTML={createMarkup(title)}
                        style={{ marginTop: '20px' }}
                    />
                )}
                <TextField
                    error={!!getFieldError('message')}
                    helperText={getFieldError('message')}
                    id="message"
                    label={
                        isAvgSubject
                            ? 'Plaats een opmerking (optioneel)'
                            : `${messageLabels[subject]} *`
                    }
                    multiline={true}
                    rows={5}
                    value={message}
                    onChange={(e) => setFieldValue('message', e.target.value)}
                />
                <SubTitle>{disclaimers[subject]}</SubTitle>
                {showContactPreference && (
                    <ContactPreferenceSelector
                        selectedOption={contactPreference}
                        setContactPreference={(contactPreference) =>
                            setFieldValue(
                                'contactPreference',
                                contactPreference
                            )
                        }
                    />
                )}
                {!isAvgSubject && (
                    <FileUploader file={file} onFileChange={onFileChange} />
                )}
                <Footnote>* Verplicht in te vullen</Footnote>
            </StepContainer>
            {submitError ? (
                <ErrorBox>{submitError}</ErrorBox>
            ) : (
                <SubmitButton disabled={submitButtonDisabled}>
                    {loading ? <Spinner /> : 'Versturen'}
                </SubmitButton>
            )}
        </div>
    );
};

export const MessageForm = styled(BaseMessageForm)`
    ${StepContainer} {
        padding-top: 40px;
        margin-bottom: 32px;
    }

    h3 {
        margin-bottom: 24px;
    }

    a {
        color: ${colors.black};
    }

    ${Disclaimer} {
        color: ${colors.red};
        font-size: 15px;
        font-weight: 300;
        line-height: 1.5;
    }

    ${CheckboxContainer} {
        margin-bottom: 40px;
    }

    ${ContactPreferenceSelector} {
        margin-bottom: 40px;
    }

    ${ErrorBox} {
        margin-bottom: 32px;
    }

    ${Footnote} {
        margin-top: 24px;

        @media (${breakpoints.mediumMin}) {
            margin-left: auto;
            margin-top: 0;
        }
    }
`;
